// Sign-off och granskningskommentarer per dokument.
import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, MessageSquare, RotateCcw, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { AiPolishInline } from "@/components/common/AiPolishInline";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export type ReviewAction = "godkand" | "komplettering" | "kommentar";

export interface DocumentReview {
  id: string;
  doc_number: string;
  action: ReviewAction;
  comment: string | null;
  reviewer_id: string;
  reviewer_name: string | null;
  reviewer_role: string | null;
  created_at: string;
}

export const REVIEW_ACTION_LABEL: Record<ReviewAction, string> = {
  godkand: "Godkänt",
  komplettering: "Komplettering begärd",
  kommentar: "Kommentar",
};

export const reviewBadgeVariant = (action: ReviewAction) =>
  action === "godkand" ? "default" : action === "komplettering" ? "destructive" : "secondary";

interface Props {
  projectId: string;
  docNumber: string;
  docTitle: string;
  /** Rollen som granskar (Projektledare/Uppdragsledare). */
  reviewerRole?: string;
  /** Får lämna sign-off/kommentar. */
  canReview: boolean;
  reviews?: DocumentReview[];
  onChanged?: () => void;
}

const DocumentReviewPanel: React.FC<Props> = ({
  projectId,
  docNumber,
  docTitle,
  reviewerRole,
  canReview,
  reviews: externalReviews,
  onChanged,
}) => {
  const [reviews, setReviews] = useState<DocumentReview[]>(externalReviews ?? []);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [uid, setUid] = useState<string | null>(null);

  useEffect(() => {
    if (externalReviews) setReviews(externalReviews);
  }, [externalReviews]);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("document_reviews")
      .select("*")
      .eq("project_id", projectId)
      .eq("doc_number", docNumber)
      .order("created_at", { ascending: false });
    setReviews((data ?? []) as DocumentReview[]);
  }, [projectId, docNumber]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUid(data.user?.id ?? null));
    if (!externalReviews) load();
  }, [load, externalReviews]);

  const submit = async (action: ReviewAction) => {
    if (action !== "godkand" && !comment.trim()) {
      toast.error("Skriv en kommentar först");
      return;
    }
    setSaving(true);
    const { data: auth } = await supabase.auth.getUser();
    const user = auth.user;
    if (!user) {
      setSaving(false);
      toast.error("Du måste vara inloggad");
      return;
    }
    const { data: profile } = await supabase
      .from("user_profiles")
      .select("first_name, last_name")
      .eq("id", user.id)
      .maybeSingle();
    const name =
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || user.email || "Granskare";

    const { error } = await supabase.from("document_reviews").insert({
      project_id: projectId,
      doc_number: docNumber,
      action,
      comment: comment.trim() || null,
      reviewer_id: user.id,
      reviewer_name: name,
      reviewer_role: reviewerRole ?? null,
    });
    setSaving(false);
    if (error) {
      toast.error("Kunde inte spara granskningen");
      return;
    }
    setComment("");
    toast.success(
      action === "godkand"
        ? `Dok ${docNumber} godkänt`
        : action === "komplettering"
          ? "Komplettering begärd"
          : "Kommentar sparad"
    );
    if (externalReviews) onChanged?.();
    else {
      await load();
      onChanged?.();
    }
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("document_reviews").delete().eq("id", id);
    if (error) {
      toast.error("Kunde inte ta bort");
      return;
    }
    if (externalReviews) onChanged?.();
    else {
      await load();
      onChanged?.();
    }
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="text-xs font-medium text-muted-foreground">
        Granskning – Dok {docNumber} {docTitle}
      </div>

      {reviews.length === 0 && (
        <p className="text-xs text-muted-foreground">Ingen granskning registrerad ännu.</p>
      )}

      {reviews.map((r) => (
        <div key={r.id} className="rounded-md border bg-background p-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={reviewBadgeVariant(r.action)}>{REVIEW_ACTION_LABEL[r.action]}</Badge>
            <span className="text-xs text-muted-foreground">
              {r.reviewer_name ?? "Granskare"}
              {r.reviewer_role ? ` (${r.reviewer_role})` : ""} ·{" "}
              {new Date(r.created_at).toLocaleDateString("sv-SE")}
            </span>
            {uid === r.reviewer_id && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 px-2"
                onClick={() => remove(r.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          {r.comment && <p className="mt-1 whitespace-pre-wrap text-sm">{r.comment}</p>}
        </div>
      ))}

      {canReview && (
        <div className="space-y-2">
          <AiPolishInline
            value={comment}
            onChange={setComment}
            placeholder="Kommentar till dokumentet (obligatoriskt vid komplettering)"
            context={`Granskningskommentar för ${docTitle}`}
            documentType={`granskning_${docNumber}`}
            projectId={projectId}
            fieldKey="review.comment"
            fieldLabel="Granskningskommentar"
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={saving} onClick={() => submit("godkand")}>
              <CheckCircle2 className="mr-1 h-4 w-4" /> Godkänn
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={saving}
              onClick={() => submit("komplettering")}
            >
              <RotateCcw className="mr-1 h-4 w-4" /> Begär komplettering
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={saving}
              onClick={() => submit("kommentar")}
            >
              <MessageSquare className="mr-1 h-4 w-4" /> Lämna kommentar
            </Button>
          </div>
        </div>
      )}
      {!canReview && reviews.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          <Send className="mr-1 inline h-3 w-3" />
          Endast Projektledare och Uppdragsledare kan godkänna eller begära komplettering.
        </p>
      )}
    </div>
  );
};

export default DocumentReviewPanel;
